import type { LucideIcon } from 'lucide-react';
import {
  Armchair,
  Backpack,
  Cross,
  Dumbbell,
  Footprints,
  ShoppingCart,
  SprayCan,
  Store,
  Tv,
} from 'lucide-react';

/*
  Spec 343 y decisión 1 del 3-nov (Melanie: «que no salgan los nombres de las otras»): los rubros
  («ejes») que ofrece la invitación los decide el servidor (`GET /api/auth/invitacion-demo/:codigo`
  → `ejes`), cada uno con CUÁNTAS tiendas tiene para elegir. La invitación es pública: no trae
  ningún nombre de tienda. La tienda concreta se elige adentro de Prixia (Compite › Competencia).
  Acá sólo se decide cómo se VEN: el ícono, el texto de la cantidad y la vista previa ilustrativa.
  Un eje nuevo del panel se ve con lo genérico.
*/

/** Un rubro que la invitación puede ofrecer. */
export interface EjeDeLaDemo {
  id: string;
  nombre: string;
  /**
   * Cuántas tiendas de este rubro se pueden elegir adentro (dato real del servidor). `null` si el
   * servidor no lo dijo (uno anterior): la pantalla no inventa una cantidad.
   */
  tiendasDisponibles: number | null;
}

/** Lo que responde el servidor al revisar el código de una invitación. */
export interface RespuestaDeLaInvitacion<M extends string = string> {
  valida: boolean;
  motivo?: M | null;
  ejes?: EjeDeLaDemo[];
  /** Decisión 2 del 3-nov: los días de prueba que abre el alta. Un servidor anterior no lo manda. */
  diasDePrueba?: number | null;
  /** Decisión 3 del 3-nov: el socio por el que llega la invitación; `null` = texto genérico. */
  socio?: string | null;
}

const textoNoVacio = (v: unknown): v is string => typeof v === 'string' && v !== '';

/** Los rubros válidos de la respuesta, en el orden del servidor. */
export function ejesDeLaRespuesta(r: { ejes?: unknown }): EjeDeLaDemo[] {
  if (!Array.isArray(r.ejes)) return [];
  return r.ejes
    .filter((e: unknown) => {
      const x = e as Partial<EjeDeLaDemo> | null;
      return !!x && textoNoVacio(x.id) && textoNoVacio(x.nombre);
    })
    .map((e) => {
      const x = e as EjeDeLaDemo;
      const n = x.tiendasDisponibles;
      return {
        id: x.id,
        nombre: x.nombre,
        tiendasDisponibles: Number.isInteger(n) && (n as number) >= 0 ? (n as number) : null,
      };
    });
}

/** «1 tienda disponible» / «10 tiendas disponibles». */
export const textoDeTiendasDisponibles = (n: number): string =>
  n === 1 ? '1 tienda disponible' : `${n} tiendas disponibles`;

/** Los días de prueba por omisión: los mismos del servidor (`DIAS_DE_LA_PRUEBA`). */
export const DIAS_DE_PRUEBA_POR_OMISION = 15;

/** Los días de la respuesta; sin un número entero positivo, los de siempre. */
export function diasDeLaRespuesta(r: { diasDePrueba?: unknown }): number {
  const d = r.diasDePrueba;
  return typeof d === 'number' && Number.isInteger(d) && d > 0 ? d : DIAS_DE_PRUEBA_POR_OMISION;
}

/** El socio de la respuesta, sin espacios de más; vacío = `null`. */
export function socioDeLaRespuesta(r: { socio?: unknown }): string | null {
  return typeof r.socio === 'string' && r.socio.trim() !== '' ? r.socio.trim() : null;
}

/**
 * El plazo en palabras, como se dice en el pase: los múltiplos de 30 son meses y los de 365, años
 * (90 → «3 meses»); lo demás, en días (15 → «15 días»).
 */
export function textoDelPlazo(dias: number): string {
  if (dias % 365 === 0) return dias === 365 ? '1 año' : `${dias / 365} años`;
  if (dias % 30 === 0) return dias === 30 ? '1 mes' : `${dias / 30} meses`;
  return dias === 1 ? '1 día' : `${dias} días`;
}

/**
 * Decisión 3 del 3-nov: la frase del pase. Con socio, «Accede a Prixia como beneficio exclusivo por tu
 * suscripción a EGX One»; sin socio, una genérica que no nombra a nadie.
 */
export function fraseDelPase(socio: string | null): string {
  return socio
    ? `Accede a Prixia como beneficio exclusivo por tu suscripción a ${socio}`
    : 'Accede a Prixia con esta invitación exclusiva';
}

const ICONOS: Record<string, LucideIcon> = {
  retail: Tv,
  farmacia: Cross,
  supermercado: ShoppingCart,
  hogar: Armchair,
  zapatos: Footprints,
  deportes: Dumbbell,
  perfumeria: SprayCan,
  escolar: Backpack,
};

export const iconoDelEje = (id: string): LucideIcon => ICONOS[id] ?? Store;

/** [nombre, precio en Bs, diferencia en % contra la tienda]: sólo para la vista previa. */
export type FilaIlustrativa = [string, number, number];

const ILUSTRATIVOS: Record<string, FilaIlustrativa[]> = {
  retail: [
    ['Smart TV 55" 4K UHD', 4299, 12],
    ['Refrigerador No Frost 360 L', 6890, -6],
    ['Barra de sonido 2.1', 1890, 3],
    ['Notebook 15" i5 16 GB', 6290, -4],
  ],
  farmacia: [
    ['Paracetamol 500 mg x 20', 9.5, 8],
    ['Protector solar FPS 50', 129, -5],
    ['Vitamina C 1 g x 10', 38, 2],
    ['Alcohol en gel 500 ml', 25, -9],
  ],
  supermercado: [
    ['Aceite de girasol 900 ml', 16.5, 6],
    ['Arroz grano largo 5 kg', 52, -3],
    ['Café molido 500 g', 48, 9],
    ['Detergente 2 kg', 42, -2],
  ],
  hogar: [
    ['Olla a presión 6 L', 389, 11],
    ['Licuadora 1,5 L', 349, -4],
    ['Juego de sábanas 2 plazas', 249, 5],
    ['Ventilador de pie 18"', 299, -7],
  ],
  zapatos: [
    ['Zapatilla urbana de cuero', 459, 8],
    ['Bota de trabajo punta de acero', 389, -5],
    ['Sandalia de mujer', 149, 3],
    ['Zapato escolar negro', 199, -7],
  ],
  deportes: [
    ['Pelota de fútbol N.º 5', 189, 6],
    ['Mancuernas 2 x 5 kg', 279, -4],
    ['Bicicleta aro 29', 2890, 10],
    ['Colchoneta de yoga', 129, -3],
  ],
  perfumeria: [
    ['Perfume 100 ml', 699, 12],
    ['Crema hidratante 200 ml', 89, -6],
    ['Shampoo 750 ml', 42, 4],
    ['Desodorante en aerosol', 29, -2],
  ],
  escolar: [
    ['Mochila escolar 18"', 249, 7],
    ['Cuaderno universitario 100 hojas', 18.5, -4],
    ['Lápices de color x 24', 45, 5],
    ['Calculadora científica', 159, -8],
  ],
};

/** Claramente ficticias: para un eje que la pantalla todavía no conoce. */
const GENERICOS: FilaIlustrativa[] = [
  ['Tu producto más vendido', 120, 9],
  ['Un producto de temporada', 85, -4],
  ['Un producto en oferta', 49.9, 6],
  ['Un producto nuevo', 210, -2],
];

export const ejemplosIlustrativos = (id: string): FilaIlustrativa[] =>
  ILUSTRATIVOS[id] ?? GENERICOS;

/** P4 de Sebastián: sin rubros listos, el alta no sigue. */
export const SIN_EJES =
  'Todavía no hay rubros listos para la prueba. Escríbenos y te avisamos apenas esté.';

/**
  La prueba no puede empezar: no hay rubros o ninguno tiene tiendas para elegir. Una sola condición
  para el aviso y para apagar «Seguir».
*/
export function sinOpcionesParaLaPrueba(ejes: EjeDeLaDemo[]): boolean {
  return !ejes.some((e) => e.tiendasDisponibles !== 0);
}

/** El rubro o la tienda elegidos dejaron de ofrecerse mientras se completaba el alta (anexo §1.2). */
export function opcionNoDisponible(err: unknown): 'EJE' | 'TIENDA' | null {
  const r = (err as { response?: { status?: number; data?: { codigo?: unknown } } })?.response;
  if (r?.status !== 422) return null;
  if (r.data?.codigo === 'EJE_NO_DISPONIBLE') return 'EJE';
  if (r.data?.codigo === 'TIENDA_NO_DISPONIBLE') return 'TIENDA';
  return null;
}
